import * as Sentry from '@sentry/browser'

import { CODE_ISO, CODES, formatByISO, ISO_CODE, ISO_FLAG, ISOs, USA_ISO } from 'app/constants/phoneCountryCodes'
import { CountriesCodes } from 'store/records/organization'

export const removeSymbolsFromPhone = phone => phone && phone.replace(/\(|\)|-|\s/gi, '')

const disableLetters = phone => phone && phone.replace(/[^0-9-()\s]+/, '')

export const parsePhoneBeforeSend = phone => {
  if (!phone) return
  const parsedPhone = removeSymbolsFromPhone(phone)
  const parsedPhoneLength = parsedPhone.length

  // if UA phone with code without +
  if (parsedPhone.slice(0, 2) === '38' && parsedPhoneLength > 10) {
    return '+' + parsedPhone
  }

  // if US phone with code without +
  if (parsedPhone[0] === '1' && parsedPhoneLength === 11) {
    return '+' + parsedPhone
  }

  // if GB phone with code without +
  if (parsedPhone.slice(0, 2) === '44' && parsedPhoneLength > 10) {
    return '+' + parsedPhone
  }

  // if correct US phone number
  if (parsedPhone[0] === '+' && parsedPhoneLength === 12) {
    return parsedPhone
  }

  if (parsedPhone.slice(0, 3) === '+34' && parsedPhoneLength >= 11) {
    return parsedPhone
  }

  // for default phone without code country
  if (parsedPhoneLength === 10 && parsedPhone.slice(0, 2) !== ISO_CODE.usa && parsedPhone[0] !== '+') {
    return `${ISO_CODE.usa}${parsedPhone}`
  }

  return parsedPhone
}

// input phone with country code
// output phoneISO
export const matchISO = phoneWithISO => {
  if (!phoneWithISO) return
  if (phoneWithISO[0] !== '+') {
    phoneWithISO = '+' + phoneWithISO
  }
  const phoneCode = CODES.find(el => phoneWithISO.includes(el))
  if (!phoneCode) {
    Sentry.withScope(scope => {
      scope.setExtra('where error was happen', 'matchISO')
      Sentry.captureException(phoneWithISO)
    })
  }
  return CODE_ISO[phoneCode]
}

export const removePhoneCodeFromPhone = (phoneWithCode, iso = USA_ISO) => {
  // remove phone
  if (!phoneWithCode) {
    return ''
  }
  const code = ISO_CODE[iso]
  return phoneWithCode.slice(code.length)
}

export const parsePhoneBeforeShow = (phoneWithoutCode, iso) => {
  if (!phoneWithoutCode) return ''

  // remove phoneCode if exists
  if (phoneWithoutCode[0] === '+') {
    phoneWithoutCode = removePhoneCodeFromPhone(phoneWithoutCode, iso)
  }
  phoneWithoutCode = removeSymbolsFromPhone(phoneWithoutCode)
  if (iso === USA_ISO) {
    phoneWithoutCode =
      '(' + phoneWithoutCode.slice(0, 3) + ') ' + phoneWithoutCode.slice(3, 6) + '-' + phoneWithoutCode.slice(6)
    return phoneWithoutCode
  }
  return phoneWithoutCode
}

export const normalizePhoneBeforeInsertForUSA = (value, previousValue) => {
  if (!value) return value
  const len = value.length
  const format = !!value.match(/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/)

  // if switch from 10 or less phone number
  if (value[0] !== '(' && value[9] === '-' && len > 10) return parsePhoneBeforeShow(value, USA_ISO)

  if (len > 14 && value.match(/\(|\)|-|\s/gi) && !format) return previousValue
  // if switch from 11 phone number
  if (!value.match(/\(|\)|-|\s/gi) && len > 10) return
  // return if last character is not digit, brackets, dash
  if (value && !/\d|\(|\)|-|\s/gi.test(value[len - 1])) return
  // length === 10 and all of them are digits
  if (len === 10 && /\d{10}/gi.test(value)) {
    return parsePhoneBeforeShow(value, USA_ISO)
  }
  if (len === 3 && value[0] !== '(') {
    return '(' + value + ') '
  } else if (len === 4 && value[0] === '(' && previousValue.length === 3) {
    return value + ') '
  } else if (len === 9 && value[len - 1] !== '-' && previousValue.length === 8) {
    return value + '-'
  } else {
    return value
  }
}

export const normalizePhoneBeforeInsertByLength = (maxLength, value = '', previousValue) => {
  if (!value) return value
  const phone = removeSymbolsFromPhone(value)
  const prev = removeSymbolsFromPhone(previousValue)
  const phoneLength = phone.length

  if (phone && phone.length > maxLength) return prev

  if (phoneLength > maxLength && !value.match(/\(|\)|-|\s/gi)) return previousValue

  // return if last character is not digit, brackets, dash
  if (phone && !/\d/gi.test(phone[phoneLength - 1])) return
  return phone
}

export const normalizePhoneBeforeInsert = (iso, value, previousValue) => {
  const phone = disableLetters(value)
  const phoneFormat = formatByISO[iso]
  if (!phoneFormat) {
    iso = USA_ISO
    Sentry.withScope(scope => {
      scope.setExtra('errorInfo', "For some reason phoneFormat didn't find")
      scope.setExtra('where error was happen', 'normalizePhoneBeforeInsert')
      scope.setExtra('payload', { iso, value, previousValue })
      Sentry.captureException(this)
    })
  }

  switch (iso) {
    case ISOs[USA_ISO]:
      return normalizePhoneBeforeInsertForUSA(phone, previousValue)
    default:
      return normalizePhoneBeforeInsertByLength(phoneFormat.max, phone, previousValue)
  }
}

// input [{ country: string, code: string }]
// output { country1: bool, country2: bool }
export const mapCountriesCodes = countriesCodes => {
  if (!countriesCodes.length) {
    return new CountriesCodes({ [USA_ISO]: true })
  }
  const isos = countriesCodes.reduce((acc, countryCode) => {
    acc[countryCode.country] = true
    return acc
  }, {})

  return new CountriesCodes(isos)
}

// countriesCodes is List of Map({ country: 'usa', code: '+1' })
// return ListOf({ label: <USA>, value: '+1' })
export const mapCountriesCodeForSelect = countriesCodes => {
  return countriesCodes.map(countryCode => {
    const iso = countryCode.get('country')
    return { label: ISO_FLAG[iso], value: iso }
  })
}

// objCountriesCodes is new CountriesCodes() from 'store/records/organization'
// output [{ country: string, code: string }]
export const mapObjCountriesCodes = objCountriesCodes => {
  const arrCountriesCodes = []
  for (const property in objCountriesCodes) {
    if (objCountriesCodes[property]) {
      arrCountriesCodes.push({ country: property, code: ISO_CODE[property] })
    }
  }
  return arrCountriesCodes
}
